import React, { useEffect, useState } from 'react';
import { Bell, BellOff, Pause, Play, SkipForward } from 'lucide-react';
import { formatClock } from './playerLogic';
import { requestNotificationPermission } from './useRestTimer';

// O descanso entre séries: um anel que se esvazia, o tempo grande, e os controlos que interessam — pausar/retomar, ±15 s e terminar.
// Fica colado ao fundo do ecrã para não tapar a próxima série. Quando acaba, vibra (se o aparelho deixar) e diz-o.

const R = 46;
const C = 2 * Math.PI * R;

export default function RestTimer({ timer, nextLabel }) {
  const [notify, setNotify] = useState(() => typeof Notification !== 'undefined' && Notification.permission === 'granted');

  // Pede licença para notificar só quando o sócio toca no sino (nunca sozinho).
  const toggleNotify = async () => {
    const result = await requestNotificationPermission();
    setNotify(result === 'granted');
  };

  const [announce, setAnnounce] = useState('');
  useEffect(() => {
    if (timer.ended) setAnnounce('Descanso terminado.');
    else if (timer.active) setAnnounce(`Descanso de ${timer.total} segundos.`);
    else setAnnounce('');
    // O anúncio só muda quando o descanso começa ou acaba; o tempo a correr não se lê em voz alta de segundo a segundo.
  }, [timer.ended, timer.active]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!timer.active) return null;

  const dash = C * (timer.ended ? 0 : 1 - timer.fraction);

  return (
    <section className={`wp-rest ${timer.ended ? 'is-ended' : ''} ${timer.paused ? 'is-paused' : ''}`} aria-label="Descanso" data-testid="rest-timer">
      <div className="wp-visually-hidden" role="status" aria-live="polite">{announce}</div>

      <div className="wp-rest-ring" aria-hidden="true">
        <svg viewBox="0 0 100 100" width="104" height="104">
          <circle className="wp-ring-track" cx="50" cy="50" r={R} />
          <circle className="wp-ring-bar" cx="50" cy="50" r={R} strokeDasharray={C} strokeDashoffset={C - dash} transform="rotate(-90 50 50)" />
        </svg>
        <span className="wp-rest-time" data-testid="rest-remaining">{timer.ended ? '0:00' : formatClock(timer.remaining)}</span>
      </div>

      <div className="wp-rest-main">
        <p className="wp-rest-title">
          {timer.ended ? 'Descanso terminado' : timer.paused ? 'Descanso em pausa' : 'Descanso'}
        </p>
        {nextLabel ? <p className="wp-rest-next">A seguir: {nextLabel}</p> : null}

        {timer.ended ? (
          <button type="button" className="wp-btn wp-btn-primary" onClick={timer.finish} data-testid="rest-go" data-autofocus>Vamos! <SkipForward size={18} aria-hidden="true" /></button>
        ) : (
          <div className="wp-rest-controls">
            <button type="button" className="wp-pill-btn" onClick={() => timer.add(-15)} aria-label="Menos 15 segundos">−15 s</button>
            {timer.paused ? (
              <button type="button" className="wp-pill-btn wp-pill-main" onClick={timer.resume} data-testid="rest-resume" aria-label="Retomar o descanso"><Play size={18} fill="currentColor" aria-hidden="true" /> <span className="wp-pill-label">Retomar</span></button>
            ) : (
              <button type="button" className="wp-pill-btn wp-pill-main" onClick={timer.pause} data-testid="rest-pause" aria-label="Pausar o descanso"><Pause size={18} fill="currentColor" aria-hidden="true" /> <span className="wp-pill-label">Pausar</span></button>
            )}
            <button type="button" className="wp-pill-btn" onClick={() => timer.add(15)} aria-label="Mais 15 segundos">+15 s</button>
            <button type="button" className="wp-pill-btn wp-pill-end" onClick={timer.finish} data-testid="rest-finish" aria-label="Terminar o descanso"><SkipForward size={18} aria-hidden="true" /> <span className="wp-pill-label">Terminar</span></button>
          </div>
        )}
      </div>

      {typeof Notification !== 'undefined' && !timer.ended ? (
        <button type="button" className="wp-icon-btn wp-rest-bell" onClick={toggleNotify} aria-pressed={notify} aria-label={notify ? 'Avisos do descanso ligados' : 'Ligar avisos do descanso'}>
          {notify ? <Bell size={20} aria-hidden="true" /> : <BellOff size={20} aria-hidden="true" />}
        </button>
      ) : null}
    </section>
  );
}
